import { DatePipe } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { FinapiTransaction } from '@core/models/finapi-transaction';
import { Kundendaten, Psd2Daten } from './kws';
import { ApiService } from '@core/services/api.service';
import { AuthService } from '@core/services/auth.service';

const KWS_TIMESTAMP_FORMAT = "yyyy-MM-dd'T'HH:mm:ss.SSSZ";
const KWS_DATE_FORMAT = 'yyyy-MM-dd';

@Injectable({
  providedIn: 'root',
})
export class KwsService {
  kundendaten: Kundendaten = {} as Kundendaten;

  private readonly http = inject(HttpClient);
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly datePipe = inject(DatePipe);

  generateSignatur(): Observable<any> {
    return this.api.post('kwsapi/kontoWechselService', { kundendaten: this.kundendaten });
  }

  /** Postet an die externe KWS-SSO-URL, deshalb ohne ApiService. */
  postTranscations(url: string, signatur: string): Observable<any> {
    const params = new HttpParams({
      fromObject: {
        kundendaten: JSON.stringify(this.kundendaten),
        signatur,
      },
    });

    return this.http.post(url, params, {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      withCredentials: true,
    });
  }

  buildKundenDaten(): void {
    const natPerson = this.auth.user.natPersonen[this.auth.index];
    const iban = this.auth.user.iban;
    const psd2Daten = this.buildPsd2Daten(
      this.auth.user.finapi.transactions,
      this.auth.user.finapi.selectedAccountList[0]?.iban ?? ''
    );

    const adresse = this.getStrasseAndHausNr(natPerson.Adresse.Strasse);
    const hasAbweichendeAnschrift = !!natPerson.Firmenanschrift.Strasse;
    const arbeitgeberAnschrift = hasAbweichendeAnschrift ? natPerson.Firmenanschrift : natPerson.Adresse;
    const arbeitgeberAdresse = hasAbweichendeAnschrift
      ? this.getStrasseAndHausNr(natPerson.Firmenanschrift.Strasse)
      : adresse;

    this.kundendaten = {
      Zeitstempel: this.datePipe.transform(new Date(), KWS_TIMESTAMP_FORMAT) || '',
      TeilnehmerTyp: 'Kontoinhaber1',
      UserRegistrationDTO: {
        Iban: iban,
        Kontonummer: iban.slice(-10),
        BLZ: iban.slice(4, 12),
        Gemeinschaftskonto: false,
        Kontoinhaber1: {
          Adresse: {
            Plz: natPerson.Adresse.PLZ,
            Ort: natPerson.Adresse.Ort,
            Strasse: adresse.street,
            Hausnummer: adresse.houseNumber,
          },
          Anrede: natPerson.Person.Anrede,
          Vorname: natPerson.Person.Vorname,
          Nachname: natPerson.Person.Nachname,
          Geburtsdatum: natPerson.Person.Geburtsdatum,
          Arbeitgeber: {
            Name: natPerson.Firmenname,
            ArbeitgeberAnsprechpartner: `${natPerson.Person.Titel} ${natPerson.Person.Vorname}`,
            ArbeitgeberAdresse: {
              Plz: arbeitgeberAnschrift.PLZ,
              Ort: arbeitgeberAnschrift.Ort,
              Strasse: arbeitgeberAdresse.street,
              Hausnummer: arbeitgeberAdresse.houseNumber,
            },
          },
          PSD2Daten: psd2Daten,
        },
        Kundentyp: 'Neukunde',
        UebertragungstypId: 2,
        Umstellungsdatum: this.datePipe.transform(new Date(), KWS_DATE_FORMAT) || '',
      },
    };
  }

  getStrasseAndHausNr(address: string): { houseNumber: string; street: string } {
    const parts = (address ?? '').trim().split(' ');
    const last = parts[parts.length - 1] ?? '';

    if (/^[0-9]+(-[a-zA-Z0-9]+)?$/.test(last)) {
      parts.pop();
      return { houseNumber: last, street: parts.join(' ') };
    }

    const beforeLast = parts[parts.length - 2] ?? '';
    parts.splice(-2, 2);
    return { houseNumber: `${beforeLast} ${last}`.trim(), street: parts.join(' ') };
  }

  private buildPsd2Daten(transactions: FinapiTransaction[] = [], selectedAccountIban: string): Psd2Daten[] {
    return transactions.map((transaction) => ({
      transactionId: transaction.id,
      buchungsdatum: this.datePipe.transform(transaction.bankBookingDate, KWS_DATE_FORMAT) || '',
      buchungsbetrag: transaction.amount.toFixed(2),
      verwendungszweck: transaction.purpose,
      kundenIban: selectedAccountIban,
      glaeubigerId: transaction.counterpartCreditorId ?? '',
      mandatsreferenz: transaction.counterpartMandateReference ?? '',
      waehrung: transaction.currency ?? '',
      zahlungspartnerIban: transaction.counterpartIban ?? '',
      zahlungspartnerName: transaction.counterpartName ?? '',
    }));
  }
}
